<template>
	<div v-if="isMobileOrTablet">
		<select :id="context.id" v-model="context._value!" :class="context.classes.input" class="form-select" @input="handleInput">
			<option
				v-for="option in context.options"
				:key="option.id"
				v-bind="option.attrs"
			>
				{{ option.label }}
			</option>
		</select>
	</div>
	<div v-else class="relative">
		<button
			:id="context.id"
			ref="buttonRef"
			type="button"
			class="flex items-center"
			:class="[
				context.classes.input,
				{'border border-border-select !bg-background-primary': isOpen},
				{'text-body-primary': context.value}
			]"
			@click="toggleOpen()"
		>
			{{ context.options?.find(option => option.value === context.value)?.label || context.placeholder || 'Vybrat' }}

			<NuxtIcon
				v-if="isOpen"
				name="x"
				:class="props.context.classes.inputIcon"
				class="text-lg text-icon-primary transition-transform"
				filled
			/>
			<NuxtIcon
				v-else
				name="chevron-down"
				:class="props.context.classes.inputIcon"
				class="text-lg text-icon-tertiary transition-transform"
				filled
			/>
		</button>

		<ClientTeleport>
			<Transition name="fade" mode="out-in">
				<div
					v-if="isOpen"
					ref="dropdownRef"
					:style="{...floatingStyles, width: buttonWidth + 'px'}"
					class="absolute z-30 bg-background-primary rounded-lg shadow-sm border border-border-secondary typo-body-s"
				>
					<UseFocusTrap :options="{immediate: true, onDeactivate: () => isOpen = false}">
						<ul
							class="max-h-32 overflow-y-auto p-4 w-full"
							role="listbox"
						>
							<li
								v-for="option in context.options"
								v-show="!option.attrs?.['hidden']"
								:key="option.value"
								class="cursor-pointer hover:text-body-important block py-1 rounded-sm focus-outline"
								:class="context.value === option.value ? 'text-body-important' : 'text-body-secondary'"
								role="option"
								:aria-selected="context.value === option.value"
								tabindex="0"
								v-bind="option.attrs"
								@click="handleList(option)"
								@keydown.prevent.space.enter="handleList(option)"
							>
								{{ option.label }}
							</li>
						</ul>
					</UseFocusTrap>
				</div>
			</Transition>
		</ClientTeleport>
	</div>
</template>

<script setup lang="ts">
	import {useFloating, offset, shift, flip} from '@floating-ui/vue';
	import type {FormKitFrameworkContext} from '@formkit/core';
	import {onClickOutside} from '@vueuse/core';
	import {UseFocusTrap} from '@vueuse/integrations/useFocusTrap/component';

	type DropdownOption = {
		value: string | number;
		label: string;
	};
	type Props = {context: FormKitFrameworkContext};

	const props = defineProps<Props>();
	const dropdownRef = ref<HTMLElement | null>(null);
	const buttonRef = ref<HTMLElement | null>(null);
	const [isOpen, toggleOpen] = useToggle();
	const {isMobileOrTablet} = useDevice();
	const {width: buttonWidth} = useElementSize(buttonRef, undefined, {box: 'border-box'});

	const {floatingStyles} = useFloating(buttonRef, dropdownRef, {middleware: [offset(4), flip(), shift({padding: 20})]});

	onClickOutside(dropdownRef, () => (isOpen.value = false), {ignore: [buttonRef]});

	function handleInput(e: Event) {
		props.context.node.input((e.target as HTMLSelectElement).value);
		isOpen.value = false;
	}

	function handleList(option: DropdownOption) {
		props.context.node.input(option.value);
		isOpen.value = false;
	}
</script>
